/**
 * Detect Vite / webpack code-split failures that mean the browser is holding a
 * stale HTML/JS shell after a deploy (hashed assets have been replaced).
 *
 * Vite specifically throws "Unable to preload CSS for /assets/…" when a lazy
 * chunk’s companion stylesheet fails to preload — that used to fall through to
 * the ErrorBoundary as a raw technical message instead of self-healing.
 */
/** Removed tour/HMR leftovers that crash until a full reload picks up the new module. */
const STALE_REFERENCE_TOKENS = [
  'onRestartTour',
  'resetSaasPublicSiteTour',
  'publicSiteTourKey',
  'SaasPublicSiteTour',
];

export function isStaleModuleReferenceError(error: unknown): boolean {
  if (!error) return false;
  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);
  const combined = `${name} ${message}`;
  if (!combined.includes('is not defined') && name !== 'ReferenceError') return false;
  return STALE_REFERENCE_TOKENS.some((token) => combined.includes(token));
}

/** realtime-js throw when `.on()` is called on a channel that already joined. */
export function isRealtimeSubscribeOrderError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);
  return (
    message.includes("cannot add 'postgres_changes' callbacks") && message.includes("after 'subscribe()'")
  );
}

export const CHUNK_RELOAD_GUARD_KEY = 'chunk-error-reloaded';
export const PAGE_REFRESH_GUARD_KEY = 'page-has-been-force-refreshed';

export function clearStaleBundleGuards(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    window.sessionStorage.removeItem(PAGE_REFRESH_GUARD_KEY);
    window.localStorage.removeItem('last_reload_timestamp');
  } catch {
    /* private mode */
  }
}

export function alreadyTriedStaleBundleReload(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Drop stale-chunk guards and load this URL from the live server, not a cached shell. */
export function reloadFreshApp(): void {
  if (typeof window === 'undefined') return;
  clearStaleBundleGuards();
  const url = new URL(window.location.href);
  url.searchParams.set('_fresh', String(Date.now()));
  window.location.replace(url.toString());
}

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);

  const combined = `${name} ${message}`;

  return (
    name === 'ChunkLoadError' ||
    combined.includes('Failed to fetch dynamically imported module') ||
    combined.includes('error loading dynamically imported module') ||
    combined.includes('Importing a module script failed') ||
    combined.includes('Loading chunk') ||
    combined.includes('Loading CSS chunk') ||
    combined.includes('Unable to preload CSS') ||
    combined.includes('ChunkLoadError') ||
    isStaleModuleReferenceError(error)
  );
}
